import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';

// ─── Data ───────────────────────────────────────────────────────────────────

type Category = 'WhatsApp API' | 'Flow Builder' | 'Database & Import' | 'Server & Deploy' | 'Billing & Account' | 'Firebase & Auth';

interface ErrorEntry {
  id: string;
  title: string;
  errorMessage: string;
  category: Category;
  cause: string;
  solution: string;
  tags: string[];
}

const ERRORS: ErrorEntry[] = [
  // ── WhatsApp API ──────────────────────────────────────────────────────────
  {
    id: 'wa-001',
    title: 'Duplicated row id',
    errorMessage: '(#131009) Parameter value is not valid — Duplicated row id',
    category: 'WhatsApp API',
    cause: 'Two or more items in the list share the same row ID. This happens when a database table has duplicate values in the id column, causing two rows to generate the same WhatsApp list row ID.',
    solution: 'Ensure every row in your database table has a unique id value. If you are importing a CSV, check for duplicates in the id column before import. The server now uses position-based IDs (dynamic_0, dynamic_1…) to avoid this, so make sure you are running the latest server version.',
    tags: ['list', 'dynamic list', 'row id', '131009', 'whatsapp', 'catalogue'],
  },
  {
    id: 'wa-002',
    title: 'Invalid access token (expired)',
    errorMessage: 'Invalid access token / OAuthException #190',
    category: 'WhatsApp API',
    cause: 'The temporary access token generated from the Meta for Developers dashboard expires after 24 hours.',
    solution: 'Generate a permanent System User token: Meta Business Manager → Business Settings → System Users → create or select a user → Generate Token → select the WhatsApp app with whatsapp_business_messaging permission. Paste the new token into Bot Settings → Access Token.',
    tags: ['token', 'oauth', 'expired', '190', 'access token', 'meta'],
  },
  {
    id: 'wa-003',
    title: 'Webhook verification fails',
    errorMessage: 'Webhook verification failed — token mismatch or endpoint unreachable',
    category: 'WhatsApp API',
    cause: 'Either (a) the bot was not created before you pasted the webhook URL into Meta Console, (b) the Verify Token was retyped instead of copied, or (c) the server is down.',
    solution: '1. Create the bot in the dashboard first. 2. Copy the Webhook URL and Verify Token exactly from Bot Settings — do not retype. 3. Check the server is online (pm2 list). 4. Make sure your domain points to the correct server IP.',
    tags: ['webhook', 'verify', 'meta', 'token', 'configuration'],
  },
  {
    id: 'wa-004',
    title: 'Message too long (4096 char limit)',
    errorMessage: '(#100) Invalid parameter — message body exceeds the character limit',
    category: 'WhatsApp API',
    cause: 'WhatsApp limits text messages to 4096 characters. A flow response or success message that is too long will be rejected.',
    solution: 'Shorten the message content in the Flow Builder. Split long content into multiple text responses separated by a 0.5s delay. Knowledge base answers from the AI bot are automatically truncated — if they are being cut off, reduce the knowledge base text density.',
    tags: ['100', 'character limit', 'message length', 'invalid parameter'],
  },
  {
    id: 'wa-005',
    title: 'Template not approved',
    errorMessage: '(#130472) Template has not been approved yet',
    category: 'WhatsApp API',
    cause: 'You are trying to send a WhatsApp message template that has not been approved by Meta. Templates used outside the 24-hour conversation window must be pre-approved.',
    solution: 'Submit the template for review in Meta Business Manager → WhatsApp → Message Templates. Allow 1–3 business days for approval. Only send template messages after approval is confirmed.',
    tags: ['130472', 'template', 'approved', 'message template'],
  },
  {
    id: 'wa-006',
    title: '24-hour window expired',
    errorMessage: '(#131047) Re-engagement message — cannot send outside the 24-hour window',
    category: 'WhatsApp API',
    cause: 'WhatsApp only allows free-form messages within 24 hours of the last user message. After that window closes, you can only send pre-approved templates.',
    solution: 'Use an approved message template to re-engage the user. In the Flow Builder, set up a Campaign with a template to send to inactive contacts. If you do not have a template, create and submit one in Meta Business Manager.',
    tags: ['131047', '24 hour', 'window', 're-engagement', 'template'],
  },
  {
    id: 'wa-007',
    title: 'List section title missing',
    errorMessage: '(#100) Invalid parameter — list message sections require a title',
    category: 'WhatsApp API',
    cause: 'Every section in a WhatsApp list message must have a non-empty title. A blank or missing section title causes the entire list send to fail.',
    solution: 'In the Flow Builder, open the List response and make sure every section has a title. Check for sections that were added but left blank.',
    tags: ['list', 'section', 'title', '100', 'invalid parameter'],
  },

  // ── Flow Builder ─────────────────────────────────────────────────────────
  {
    id: 'fb-001',
    title: 'No API URL configured',
    errorMessage: 'Error: No API URL configured',
    category: 'Flow Builder',
    cause: 'A Dynamic List response has no API URL set, or the saved flow in Firebase is an older version that does not include the apiUrl field.',
    solution: 'Open the Flow Builder for this bot, find the Dynamic List response, and set the API URL. Save the flow. If you recently imported a new flow JSON, make sure the import completed successfully — check the flow in Firebase to confirm the apiUrl is present.',
    tags: ['dynamic list', 'api url', 'not configured', 'flow'],
  },
  {
    id: 'fb-002',
    title: 'Item details not configured',
    errorMessage: 'Item details not configured',
    category: 'Flow Builder',
    cause: 'A user tapped an item from a Dynamic List but the flow has no dynamicResponseConfig to show item details. This occurs when using the v3+ database-backed flow where item details are built automatically — if the server is on an older version it falls back to this error.',
    solution: 'Update the server to the latest version. Database-backed dynamic lists (using /api/db/query/… URLs) build item details automatically from the cached list data — no dynamicResponseConfig is needed. If you are using a custom API, add a Dynamic Response config block pointing to your detail endpoint.',
    tags: ['item detail', 'dynamic response', 'not configured', 'dynamic list'],
  },
  {
    id: 'fb-003',
    title: '"Error occurred." before success message',
    errorMessage: 'Error occurred. [then correct success message follows]',
    category: 'Flow Builder',
    cause: 'A Webhook response with a placeholder URL (e.g. __save_booking__) is in the flow before the success message. The server tries to call that URL, fails, and sends the error message.',
    solution: 'Remove the Webhook response from the flow. Booking data is saved automatically by the server via BOOKING_PATTERNS — no webhook is needed for Firebase saves. If you do need a webhook, set a real URL.',
    tags: ['webhook', 'error occurred', 'placeholder', 'booking', '__save_booking__'],
  },
  {
    id: 'fb-004',
    title: 'Bot goes silent after ask variable',
    errorMessage: 'Bot sends a question, user replies, bot gives no response',
    category: 'Flow Builder',
    cause: 'The ask response uses a nextTrigger field (e.g. "nextTrigger": "quote_phone") but an older server version only supports the askResponses array. Since askResponses is empty, after capturing the variable nothing is sent.',
    solution: 'Update the server to the latest version which supports nextTrigger chaining. The awaitingInput handler now resolves nextTrigger by looking up the trigger in the flow and executing its responses.',
    tags: ['ask', 'variable', 'silent', 'nextTrigger', 'askResponses', 'quote flow'],
  },
  {
    id: 'fb-005',
    title: '{{variable}} not replaced in message',
    errorMessage: 'Message shows {{customer_name}} literally instead of the value',
    category: 'Flow Builder',
    cause: 'The variable was not collected before this point in the flow, the variable name in the ask field does not exactly match the {{}} placeholder (case-sensitive), or the variable was cleared when the session expired.',
    solution: 'Check that: (1) the Ask response is earlier in the flow than where the variable is used, (2) the askVariable name exactly matches what is in the {{}} — e.g. askVariable: "customer_name" and {{customer_name}} (case-sensitive). (3) The session is being persisted — update to the latest server version which saves session state to Firebase after every ask.',
    tags: ['variable', 'replacement', 'template', 'mustache', 'customer_name'],
  },
  {
    id: 'fb-006',
    title: 'Wrong trigger fires',
    errorMessage: 'Bot responds to a message with a trigger that is not relevant',
    category: 'Flow Builder',
    cause: 'Trigger matching is partial — any message containing the trigger word will match. A short trigger like "time" will match "What time does your store open?" or "overtime".',
    solution: 'Use longer, more specific trigger phrases (e.g. "opening_hours" instead of "hours", "book appointment" instead of "book"). Longer triggers with underscores are safer. Triggers are matched in order, so put more specific ones first.',
    tags: ['trigger', 'wrong trigger', 'partial match', 'keyword'],
  },
  {
    id: 'fb-007',
    title: 'Order handler fires twice',
    errorMessage: 'Customer receives the name/phone questions twice',
    category: 'Flow Builder',
    cause: 'Two triggers or responses both activate the order handler for the same message. This can happen if the order_ prefix is in a button payload AND a separate trigger also matches.',
    solution: 'Check that no two triggers both activate an order handler for the same payload. Remove duplicate order_handler responses in the flow builder.',
    tags: ['order handler', 'duplicate', 'twice', 'double'],
  },
  {
    id: 'fb-008',
    title: 'Booking not saved to Firebase',
    errorMessage: 'No booking appears in the Bookings tab after a completed enquiry',
    category: 'Flow Builder',
    cause: 'The ask variable name does not match any of the BOOKING_PATTERNS (date, time, service, booking, customer, quantity, contact, company, quote, start, etc.). Bookings are only auto-saved when a matching variable is collected.',
    solution: 'Name your ask variables to include a booking pattern keyword: customer_name, customer_phone, start_date, quantity, quote_company, etc. Alternatively, the booking is also saved when the order handler runs — make sure the action is set to admin_notification.',
    tags: ['booking', 'firebase', 'not saved', 'BOOKING_PATTERNS', 'bookings tab'],
  },

  // ── Database & Import ─────────────────────────────────────────────────────
  {
    id: 'db-001',
    title: 'CSV headers must match field names',
    errorMessage: 'Failed to import CSV: headers must match field names: id, name, format, …',
    category: 'Database & Import',
    cause: 'The CSV file has column names that do not exactly match the table field names — either extra columns, misspelled names, or different casing.',
    solution: 'Compare the CSV header row to the table field names in the Database tab. Remove any extra columns (e.g. a "category" column that is not in the table). Column names are case-sensitive and must match exactly. Export the table schema first if you are unsure of the field names.',
    tags: ['csv', 'import', 'headers', 'field names', 'column'],
  },
  {
    id: 'db-002',
    title: 'set failed: value argument contains undefined',
    errorMessage: 'Error saving flow: set failed: first argument contains undefined in property',
    category: 'Database & Import',
    cause: 'The flow JSON being saved to Firebase contains an undefined value somewhere. Firebase does not accept undefined — only null, string, number, boolean, or nested objects.',
    solution: 'Check the flow JSON for any fields that might be undefined. Common causes: a button payload that was left blank, an apiUrl field that was never set, or a JavaScript variable that was not initialized before being written to Firebase. Replace undefined values with null or empty strings.',
    tags: ['firebase', 'undefined', 'set failed', 'flow', 'json', 'save'],
  },
  {
    id: 'db-003',
    title: 'Dynamic list shows no items',
    errorMessage: 'The bot says "No items found." or the list is empty',
    category: 'Database & Import',
    cause: 'Either (a) the API URL returns no data, (b) the search/filter parameters do not match any rows, (c) the table is empty, or (d) the Title path does not match the JSON response structure.',
    solution: '1. Test the API URL directly in a browser. 2. Check the search= and field= parameters match actual column values in your table. 3. Verify the table has rows in the Database tab. 4. Check the Title path matches the JSON key (e.g. "name" not "Name").',
    tags: ['dynamic list', 'no items', 'empty', 'api', 'filter'],
  },

  // ── Server & Deploy ───────────────────────────────────────────────────────
  {
    id: 'sv-001',
    title: 'ReferenceError: X is not defined (crash loop)',
    errorMessage: 'ReferenceError: formatDynamicListFromAPI is not defined',
    category: 'Server & Deploy',
    cause: 'A stale function reference remains in module.exports after the function was renamed or removed during a refactor. Node.js fails to start and pm2 enters a restart loop.',
    solution: 'SSH into the server, check pm2 logs (pm2 logs autopilot-server --lines 20), find the undefined reference, and remove it from module.exports in the relevant file. Redeploy the fixed file and restart: pm2 restart autopilot-server.',
    tags: ['referenceError', 'not defined', 'module.exports', 'crash', 'pm2', 'loop'],
  },
  {
    id: 'sv-002',
    title: '502 Bad Gateway / CORS error',
    errorMessage: 'Access to fetch blocked by CORS policy — or — 502 Bad Gateway',
    category: 'Server & Deploy',
    cause: 'The server process has crashed or is in a restart loop. The nginx proxy returns 502 when Node.js is not listening on its port.',
    solution: 'SSH to the server and run: pm2 list (check status), pm2 logs autopilot-server --lines 30 (find the crash reason), fix the error in the code, redeploy, then pm2 restart autopilot-server. After a fix, watch pm2 monit to confirm it stays up.',
    tags: ['502', 'cors', 'bad gateway', 'crash', 'nginx', 'server down'],
  },
  {
    id: 'sv-003',
    title: 'Session lost after server restart',
    errorMessage: 'Bot asks a question, server restarts, user replies but bot ignores the answer',
    category: 'Server & Deploy',
    cause: 'Ask variable state (awaitingInput, askVariable, askResponses) was only stored in memory and not persisted to Firebase before the server restart.',
    solution: 'Update to the latest server version. The ask case in sendResponse now calls saveSession immediately after setting awaitingInput, so session state survives restarts.',
    tags: ['session', 'restart', 'ask', 'awaitingInput', 'memory', 'lost'],
  },
  {
    id: 'sv-004',
    title: 'ECONNREFUSED — server not running',
    errorMessage: 'connect ECONNREFUSED 127.0.0.1:3001',
    category: 'Server & Deploy',
    cause: 'The Node.js server process is not running.',
    solution: 'SSH to the server: pm2 start /var/www/zimchat-server/server.js --name autopilot-server (if starting fresh), or pm2 restart autopilot-server (if it exists). Check pm2 save to ensure it auto-starts on reboot.',
    tags: ['econnrefused', 'not running', 'connection refused', 'port'],
  },
  {
    id: 'sv-005',
    title: 'Deployment: git pull deletes tracked files',
    errorMessage: 'Files disappeared from production after git pull',
    category: 'Server & Deploy',
    cause: 'Running git rm --cached on files that are tracked in the shared git repo causes them to be deleted from the working tree on the production server when git pull is run.',
    solution: 'Never run git rm --cached on files that are shared between development and production unless you intend to delete them from production. If you need to untrack a file locally only, add it to .gitignore first, then run git rm --cached.',
    tags: ['git', 'git pull', 'deleted', 'git rm', 'tracked', 'production'],
  },

  // ── Billing & Account ─────────────────────────────────────────────────────
  {
    id: 'bi-001',
    title: 'Bots suspended after payment',
    errorMessage: 'Bots show "Suspended" status despite a completed payment',
    category: 'Billing & Account',
    cause: 'The plan was not activated after payment — either the webhook from the payment provider did not fire, or the account was flagged during the payment failure retry cycle.',
    solution: 'Go to Billing → check the payment status. If payment is marked as successful but bots are suspended, contact support with your payment reference. The support team can manually activate the plan. For Paynow payments, confirm the poll URL is correctly configured.',
    tags: ['suspended', 'payment', 'billing', 'plan', 'bots', 'paynow'],
  },
  {
    id: 'bi-002',
    title: 'Bot limit reached',
    errorMessage: 'Cannot create bot — plan bot limit reached',
    category: 'Billing & Account',
    cause: 'Your current plan allows a limited number of bots and you have reached that limit.',
    solution: 'Upgrade to a higher plan: Starter (1 bot) → Business (3 bots) → Agency (10 bots) → Enterprise (unlimited). Go to Billing → Upgrade. Alternatively, delete an unused bot to free up a slot.',
    tags: ['bot limit', 'plan', 'upgrade', 'cannot create', 'limit reached'],
  },
  {
    id: 'bi-003',
    title: 'AI bot not available on current plan',
    errorMessage: 'AI Bot type is not available on your current plan',
    category: 'Billing & Account',
    cause: 'AI bots require the Business plan or above. Starter and Free plans only support WhatsApp, Website, Facebook, and Voice bots.',
    solution: 'Upgrade to the Business plan to unlock AI bots. Go to Billing → Upgrade Plan.',
    tags: ['ai bot', 'plan', 'not available', 'upgrade', 'groq'],
  },

  // ── Firebase & Auth ───────────────────────────────────────────────────────
  {
    id: 'fa-001',
    title: 'PERMISSION_DENIED on template seeding',
    errorMessage: 'Firebase: PERMISSION_DENIED — templates node not allowed',
    category: 'Firebase & Auth',
    cause: 'The Firebase Realtime Database security rules do not include a rule for the templates node.',
    solution: 'In Firebase Console → Realtime Database → Rules, add: "templates": { ".read": "auth != null", ".write": "auth != null" }. Publish the rules.',
    tags: ['permission_denied', 'firebase', 'rules', 'templates', 'seeding'],
  },
  {
    id: 'fa-002',
    title: 'auth/configuration-not-found',
    errorMessage: 'Firebase: auth/configuration-not-found',
    category: 'Firebase & Auth',
    cause: 'Google Sign-In has not been enabled in the Firebase project.',
    solution: 'Firebase Console → Authentication → Sign-in method → Google → Enable. Also add your domain to the Authorized domains list.',
    tags: ['auth', 'configuration', 'google sign-in', 'firebase', 'login'],
  },
  {
    id: 'fa-003',
    title: 'auth/popup-blocked',
    errorMessage: 'Firebase: auth/popup-blocked',
    category: 'Firebase & Auth',
    cause: 'The browser blocked the Google sign-in popup.',
    solution: 'Allow popups for this site in the browser settings. Alternatively, switch from signInWithPopup to signInWithRedirect in the auth code for environments where popups are consistently blocked.',
    tags: ['auth', 'popup', 'blocked', 'google', 'sign-in'],
  },
  {
    id: 'fa-004',
    title: 'auth/unauthorized-domain',
    errorMessage: 'Firebase: auth/unauthorized-domain',
    category: 'Firebase & Auth',
    cause: 'The domain you are accessing the app from is not in the Firebase project\'s list of authorized domains.',
    solution: 'Firebase Console → Authentication → Settings → Authorized domains → Add domain. Add your production domain and localhost for development.',
    tags: ['auth', 'unauthorized', 'domain', 'firebase', 'cors'],
  },
];

const CATEGORIES: { label: Category | 'All'; color: string }[] = [
  { label: 'All', color: '#5B4FE8' },
  { label: 'WhatsApp API', color: '#25D366' },
  { label: 'Flow Builder', color: '#f59e0b' },
  { label: 'Database & Import', color: '#06b6d4' },
  { label: 'Server & Deploy', color: '#ef4444' },
  { label: 'Billing & Account', color: '#8b5cf6' },
  { label: 'Firebase & Auth', color: '#f97316' },
];

const CAT_COLOR: Record<string, string> = Object.fromEntries(CATEGORIES.map(c => [c.label, c.color]));

// ─── Component ──────────────────────────────────────────────────────────────

export default function ErrorDatabase() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState<any>(null);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => setUser(u));
    return unsub;
  }, []);

  // Press '/' to focus search
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  function copyError(entry: ErrorEntry) {
    navigator.clipboard.writeText(entry.errorMessage).catch(() => {});
    setCopied(entry.id);
    setTimeout(() => setCopied(null), 1800);
  }

  const q = query.toLowerCase();
  const filtered = ERRORS.filter(e => {
    const catMatch = activeCategory === 'All' || e.category === activeCategory;
    if (!catMatch) return false;
    if (!q) return true;
    return (
      e.title.toLowerCase().includes(q) ||
      e.errorMessage.toLowerCase().includes(q) ||
      e.cause.toLowerCase().includes(q) ||
      e.solution.toLowerCase().includes(q) ||
      e.tags.some(t => t.toLowerCase().includes(q))
    );
  });

  const highlight = (text: string) => {
    if (!q) return text;
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    return (
      text.slice(0, idx) +
      '<mark style="background:#f59e0b30;color:inherit;border-radius:2px;">' +
      text.slice(idx, idx + q.length) +
      '</mark>' +
      text.slice(idx + q.length)
    );
  };

  return (
    <>
      <SEO
        title={`Error Database | ${companyName}`}
        description="Searchable database of common errors and solutions for the Autopilot platform."
        keywords="error database, troubleshooting, fix, WhatsApp bot errors"
        type="website"
        noindex
      />

      <div style={{
        minHeight: '100vh',
        background: 'var(--bg-page)',
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>

        {/* ── Top Nav ── */}
        <div style={{
          background: 'var(--bg-sidebar)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          height: 56,
          gap: 16,
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{ background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, padding: '4px 8px', borderRadius: 6 }}
          >
            ← Dashboard
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 13, color: 'var(--text-faint)' }}>🔍 Error Database</span>
          <div style={{ flex: 1 }} />
          {user && (
            <button
              onClick={handleLogout}
              style={{ background: 'none', border: '1px solid var(--bg-element)', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 12, padding: '4px 12px', borderRadius: 6 }}
            >
              Sign out
            </button>
          )}
        </div>

        <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 20px 60px' }}>

          {/* ── Header ── */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ color: 'var(--text-primary)', fontSize: 26, fontWeight: 700, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
              🔴 Error Database
            </h1>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
              {ERRORS.length} known errors · Search by message, keyword, or error code · Press <kbd style={{ background: 'var(--bg-element)', border: '1px solid var(--border-subtle)', borderRadius: 4, padding: '1px 6px', fontSize: 11, color: 'var(--text-secondary)' }}>/</kbd> to focus
            </p>
          </div>

          {/* ── Search bar ── */}
          <div style={{ position: 'relative', marginBottom: 20 }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: 'var(--text-faint)', pointerEvents: 'none' }}>🔍</span>
            <input
              ref={searchRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setExpanded(null); }}
              placeholder="Search error message, keyword, or error code…"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '12px 16px 12px 44px',
                borderRadius: 10,
                border: '1.5px solid var(--bg-element)',
                background: 'var(--bg-card)',
                color: 'var(--text-primary)',
                fontSize: 14,
                outline: 'none',
                transition: 'border-color .15s',
              }}
              onFocus={e => (e.target.style.borderColor = '#5B4FE8')}
              onBlur={e => (e.target.style.borderColor = 'var(--bg-element)')}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-faint)', cursor: 'pointer', fontSize: 16, padding: 4 }}
              >×</button>
            )}
          </div>

          {/* ── Category chips ── */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
            {CATEGORIES.map(cat => {
              const count = cat.label === 'All' ? ERRORS.length : ERRORS.filter(e => e.category === cat.label).length;
              const active = activeCategory === cat.label;
              return (
                <button
                  key={cat.label}
                  onClick={() => { setActiveCategory(cat.label); setExpanded(null); }}
                  style={{
                    padding: '5px 14px',
                    borderRadius: 20,
                    border: `1.5px solid ${active ? cat.color : 'var(--bg-element)'}`,
                    background: active ? cat.color + '18' : 'transparent',
                    color: active ? cat.color : 'var(--text-faint)',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 600,
                    transition: 'all .15s',
                  }}
                >
                  {cat.label} <span style={{ opacity: .7, fontWeight: 400 }}>({count})</span>
                </button>
              );
            })}
          </div>

          {/* ── Results count ── */}
          {(query || activeCategory !== 'All') && (
            <div style={{ fontSize: 12, color: 'var(--text-faint)', marginBottom: 14 }}>
              {filtered.length === 0 ? 'No errors match your search.' : `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`}
            </div>
          )}

          {/* ── Error list ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filtered.length === 0 && (
              <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--bg-element)',
                borderRadius: 12,
                padding: '40px 24px',
                textAlign: 'center',
                color: 'var(--text-faint)',
              }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>🤷</div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>No errors found</div>
                <div style={{ fontSize: 13 }}>Try a different search term, or <button onClick={() => { setQuery(''); setActiveCategory('All'); }} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>clear all filters</button></div>
              </div>
            )}

            {filtered.map(entry => {
              const isOpen = expanded === entry.id;
              const color = CAT_COLOR[entry.category] || '#5B4FE8';
              return (
                <div
                  key={entry.id}
                  style={{
                    background: 'var(--bg-card)',
                    border: `1.5px solid ${isOpen ? color + '60' : 'var(--bg-element)'}`,
                    borderRadius: 12,
                    overflow: 'hidden',
                    transition: 'border-color .15s, box-shadow .15s',
                    boxShadow: isOpen ? `0 2px 16px ${color}15` : 'none',
                  }}
                >
                  {/* Row */}
                  <div
                    onClick={() => setExpanded(isOpen ? null : entry.id)}
                    style={{
                      padding: '14px 18px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 14,
                    }}
                  >
                    {/* Severity dot */}
                    <div style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: color, flexShrink: 0, marginTop: 4,
                    }} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Title */}
                      <div
                        style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)', marginBottom: 4 }}
                        dangerouslySetInnerHTML={{ __html: highlight(entry.title) }}
                      />
                      {/* Error message */}
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: 12,
                        color: color,
                        background: color + '10',
                        border: `1px solid ${color}25`,
                        borderRadius: 6,
                        padding: '4px 10px',
                        display: 'inline-block',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                        dangerouslySetInnerHTML={{ __html: highlight(entry.errorMessage) }}
                      />
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                        background: color + '18', color,
                        whiteSpace: 'nowrap',
                      }}>
                        {entry.category}
                      </span>
                      <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>{isOpen ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {/* Expanded */}
                  {isOpen && (
                    <div style={{ borderTop: `1px solid var(--bg-element)`, padding: '16px 18px', background: 'var(--bg-sidebar)' }}>
                      {/* Full error message with copy */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Error Message</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <code style={{
                            flex: 1,
                            fontFamily: 'monospace',
                            fontSize: 12,
                            color: color,
                            background: 'var(--bg-page)',
                            border: `1px solid ${color}25`,
                            borderRadius: 7,
                            padding: '8px 12px',
                            display: 'block',
                            wordBreak: 'break-all',
                          }}>
                            {entry.errorMessage}
                          </code>
                          <button
                            onClick={() => copyError(entry)}
                            style={{
                              flexShrink: 0,
                              padding: '7px 12px',
                              borderRadius: 7,
                              border: '1px solid var(--bg-element)',
                              background: copied === entry.id ? '#10b981' : 'var(--bg-card)',
                              color: copied === entry.id ? '#fff' : 'var(--text-secondary)',
                              cursor: 'pointer',
                              fontSize: 12,
                              fontWeight: 600,
                              transition: 'all .15s',
                            }}
                          >
                            {copied === entry.id ? '✓ Copied' : '📋 Copy'}
                          </button>
                        </div>
                      </div>

                      {/* Cause */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>Why it happens</div>
                        <div style={{
                          fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7,
                          background: '#f59e0b08', border: '1px solid #f59e0b20',
                          borderLeft: '3px solid #f59e0b',
                          borderRadius: 8, padding: '10px 14px',
                        }}
                          dangerouslySetInnerHTML={{ __html: highlight(entry.cause) }}
                        />
                      </div>

                      {/* Solution */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 6 }}>How to fix it</div>
                        <div style={{
                          fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7,
                          background: '#10b98108', border: '1px solid #10b98120',
                          borderLeft: '3px solid #10b981',
                          borderRadius: 8, padding: '10px 14px',
                        }}
                          dangerouslySetInnerHTML={{ __html: highlight(entry.solution) }}
                        />
                      </div>

                      {/* Tags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {entry.tags.map(tag => (
                          <button
                            key={tag}
                            onClick={() => { setQuery(tag); setActiveCategory('All'); setExpanded(null); }}
                            style={{
                              padding: '2px 9px',
                              borderRadius: 10,
                              border: '1px solid var(--bg-element)',
                              background: 'var(--bg-page)',
                              color: 'var(--text-faint)',
                              cursor: 'pointer',
                              fontSize: 11,
                              fontFamily: 'monospace',
                            }}
                          >
                            #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Footer ── */}
          <div style={{ marginTop: 40, textAlign: 'center', color: 'var(--text-faint)', fontSize: 12 }}>
            Don't see your error? Check the <button onClick={() => navigate('/documentation#troubleshooting')} style={{ background: 'none', border: 'none', color: '#818cf8', cursor: 'pointer', textDecoration: 'underline', fontSize: 12 }}>Troubleshooting docs</button> or contact <a href={`mailto:${process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw'}`} style={{ color: '#818cf8', textDecoration: 'underline' }}>support</a>.
          </div>
        </div>
      </div>
    </>
  );
}
